/**
 * Settings → Notifications (docs/plans/ADMIN_SETTINGS_SCOPE_2026_09_29.md, WS-C).
 *
 * Who hears about what. Each event starts on its defaults (exactly who received
 * it before this page existed); "Customize" turns the defaults into editable
 * recipients, "Reset" goes back. Recent sends show SendGrid's delivery result
 * per recipient, so "did that email arrive?" is answered here.
 *
 * Endpoints (routers/admin_notifications.py):
 *   GET    /api/admin/notifications/events
 *   POST   /api/admin/notifications/routes  · DELETE /api/admin/notifications/routes/:id
 *   POST   /api/admin/notifications/events/:key/customize | /reset | /test
 *   GET    /api/admin/alerts/assignees      (team members to pick from)
 */
import React, { useState } from 'react';
import { Bell, Plus, RotateCcw, Send, Trash2, Wand2 } from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { adminFetch, adminMutate } from '../../../utils/adminApi';
import { AdminMe, adminCan, errorMessage } from './settingsLogic';
import {
  NotificationEventInfo, NotificationsResponse, TargetKind, canEditEvent, deliveryLabel, filterLabel, targetLabel, validateTarget,
} from './notificationsLogic';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 20_000, refetchOnWindowFocus: false } },
});

interface TeamMember { id: string; email: string; full_name: string | null }

const EventCard: React.FC<{
  ev: NotificationEventInfo;
  areas: { key: string; label: string }[];
  team: TeamMember[];
  canEdit: boolean;
  onChanged: () => void;
}> = ({ ev, areas, team, canEdit, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [kind, setKind] = useState<TargetKind>('admin');
  const [target, setTarget] = useState('');
  const [area, setArea] = useState('');
  const [minSeverity, setMinSeverity] = useState(ev.default_min_severity ?? '');

  const run = async (fn: () => Promise<unknown>, okText?: string) => {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      if (okText) setMessage({ ok: true, text: okText });
      onChanged();
    } catch (err) {
      setMessage({ ok: false, text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const add = () => {
    const value = kind === 'admin' ? target : target.trim();
    const problem = kind === 'admin' ? (value ? null : 'Pick a team member.') : validateTarget(kind, value);
    if (problem) {
      setMessage({ ok: false, text: problem });
      return;
    }
    run(() => adminMutate('/api/admin/notifications/routes', 'POST', {
      event_key: ev.key, target_kind: kind, target: value,
      area: ev.alert_routing && area ? area : null,
      min_severity: ev.alert_routing && minSeverity ? minSeverity : null,
    }), 'Recipient added.').then(() => setTarget(''));
  };

  const customized = ev.basis === 'routes';

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 sm:max-w-xl">
          <h2 className="text-sm font-semibold text-gray-900">
            {ev.label}
            {ev.required && <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">Can't be muted</span>}
            {ev.protected && <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">Super admins manage</span>}
          </h2>
          <p className="mt-0.5 text-xs text-gray-500">{ev.description}</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-1">
            {!customized ? (
              <button type="button" disabled={busy}
                onClick={() => run(() => adminMutate(`/api/admin/notifications/events/${ev.key}/customize`, 'POST'))}
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50">
                <Wand2 className="h-3 w-3" /> Customize
              </button>
            ) : (
              <button type="button" disabled={busy}
                onClick={() => run(() => adminMutate(`/api/admin/notifications/events/${ev.key}/reset`, 'POST'), 'Back to defaults.')}
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50">
                <RotateCcw className="h-3 w-3" /> Reset to defaults
              </button>
            )}
            <button type="button" disabled={busy}
              onClick={() => run(async () => {
                const res = await adminMutate<{ status: string; emails?: string[] }>(
                  `/api/admin/notifications/events/${ev.key}/test`, 'POST');
                if (res.status !== 'sent') throw new Error(res.status === 'no_recipients' ? 'Nobody to send to.' : `Not sent (${res.status}).`);
              }, 'Test sent. Check the recent sends below in a minute.')}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50">
              <Send className="h-3 w-3" /> Send test
            </button>
          </div>
        )}
      </div>

      <div className="mt-3 text-xs">
        <div className="mb-1 font-semibold uppercase tracking-wide text-gray-400">
          {customized ? 'Recipients' : 'Recipients (defaults)'}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {customized
            ? ev.routes.map(r => (
              <span key={r.id} className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-gray-800">
                {targetLabel(r.target_kind, r.target, r.admin_email, r.admin_name)}
                {filterLabel(r.area, r.min_severity, areas) && (
                  <span className="text-gray-500">· {filterLabel(r.area, r.min_severity, areas)}</span>
                )}
                {canEdit && (
                  <button type="button" disabled={busy} aria-label="Remove recipient"
                    onClick={() => run(() => adminMutate(`/api/admin/notifications/routes/${r.id}`, 'DELETE'))}
                    className="text-gray-400 hover:text-red-600">
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))
            : ev.default_targets.map(t => (
              <span key={`${t.target_kind}:${t.target}`} className="rounded-full bg-gray-50 px-2 py-1 text-gray-600">
                {targetLabel(t.target_kind, t.target)}
                {ev.default_min_severity ? ` · ${ev.default_min_severity} only` : ''}
              </span>
            ))}
          {customized && ev.routes.length === 0 && <span className="text-gray-500">Nobody. Add a recipient.</span>}
        </div>
        {ev.effective_emails.length > 0 && (
          <div className="mt-1 text-gray-400">Emails today: {ev.effective_emails.join(', ')}</div>
        )}
      </div>

      {canEdit && customized && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <select value={kind} onChange={e => { setKind(e.target.value as TargetKind); setTarget(''); }}
            className="rounded-md border border-gray-200 px-2 py-1" aria-label="Recipient type">
            <option value="admin">Team member</option>
            <option value="email">Email address</option>
            <option value="slack_channel">Slack channel</option>
          </select>
          {kind === 'admin' ? (
            <select value={target} onChange={e => setTarget(e.target.value)}
              className="rounded-md border border-gray-200 px-2 py-1" aria-label="Team member">
              <option value="">Choose…</option>
              {team.map(m => <option key={m.id} value={m.id}>{m.full_name || m.email}</option>)}
            </select>
          ) : (
            <input value={target} onChange={e => setTarget(e.target.value)}
              placeholder={kind === 'email' ? 'name@coachcedar.com' : '#channel-name'}
              className="w-56 rounded-md border border-gray-200 px-2 py-1" aria-label="Recipient" />
          )}
          {ev.alert_routing && (
            <>
              <select value={area} onChange={e => setArea(e.target.value)}
                className="rounded-md border border-gray-200 px-2 py-1" aria-label="Area">
                <option value="">Any area</option>
                {areas.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
              <select value={minSeverity} onChange={e => setMinSeverity(e.target.value)}
                className="rounded-md border border-gray-200 px-2 py-1" aria-label="Minimum severity">
                <option value="critical">Critical only</option>
                <option value="warning">Warning or worse</option>
                <option value="">Any severity</option>
              </select>
            </>
          )}
          <button type="button" disabled={busy} onClick={add}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 font-medium text-white disabled:opacity-50"
            style={{ backgroundColor: '#1F6C4F' }}>
            <Plus className="h-3 w-3" /> Add
          </button>
        </div>
      )}

      {message && (
        <div className={`mt-2 text-xs ${message.ok ? 'text-green-700' : 'text-red-600'}`} role="status">{message.text}</div>
      )}

      {ev.recent.length > 0 && (
        <details className="mt-3 text-xs">
          <summary className="cursor-pointer text-gray-500">Recent sends ({ev.recent.length})</summary>
          <ul className="mt-2 space-y-2">
            {ev.recent.map(s => (
              <li key={s.id} className="rounded bg-gray-50 p-2">
                <div className="flex flex-wrap justify-between gap-2">
                  <span className="font-medium text-gray-800">{s.subject}</span>
                  <span className="text-gray-400">{s.created_at ? new Date(s.created_at).toLocaleString() : ''}</span>
                </div>
                {s.error && <div className="text-red-600">{s.error}</div>}
                <ul className="mt-1">
                  {s.recipients.map(r => (
                    <li key={r} className="flex justify-between gap-2 text-gray-600">
                      <span>{r}</span><span>{deliveryLabel(s, r)}</span>
                    </li>
                  ))}
                  {s.slack_channels.map(c => <li key={c} className="text-gray-600">Slack {c}</li>)}
                </ul>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
};

const NotificationsInner: React.FC = () => {
  const me = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: async () => (await adminFetch<{ admin: AdminMe }>('/api/admin/auth/me')).admin,
  });
  const data = useQuery({
    queryKey: ['admin', 'notifications'],
    queryFn: () => adminFetch<NotificationsResponse>('/api/admin/notifications/events'),
  });
  const team = useQuery({
    queryKey: ['admin', 'alerts', 'assignees'],
    queryFn: async () => (await adminFetch<{ assignees: TeamMember[] }>('/api/admin/alerts/assignees')).assignees,
    retry: false,
  });
  const canEdit = adminCan(me.data, 'settings.write');

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
        <Bell className="h-5 w-5" style={{ color: '#1F6C4F' }} /> Notifications
      </h1>
      <p className="mt-1 text-sm text-gray-500">
        Who hears about what. Each notification starts with the recipients it always had; customize it to change them.
        {data.data && <> Sent from <b>{data.data.sender.name} &lt;{data.data.sender.address}&gt;</b>{data.data.sender.reply_to && <>, replies to <b>{data.data.sender.reply_to}</b></>} (Settings → General).</>}
      </p>
      {data.isLoading && <div className="mt-6 text-sm text-gray-500">Loading…</div>}
      {data.error && <div className="mt-6 rounded-md bg-red-50 p-3 text-sm text-red-700">{errorMessage(data.error)}</div>}
      <div className="mt-4 space-y-4">
        {(data.data?.events ?? []).map(ev => (
          <EventCard key={ev.key} ev={ev} areas={data.data?.areas ?? []} team={team.data ?? []}
            canEdit={canEditEvent(ev, canEdit, me.data?.role)} onChanged={() => data.refetch()} />
        ))}
      </div>
    </div>
  );
};

const SettingsNotifications: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <NotificationsInner />
  </QueryClientProvider>
);

export default SettingsNotifications;
